import React, { useState } from 'react';
import clsx from 'clsx';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';
import { useQuickExit } from './QuickExitBlock.utils';
import { IProps} from './QuickExitBlock.interface';
import './QuickExitBlock.styles.css';

const QuickExitBlock: React.FC<IProps> = ({
  title = 'Quick Exit',
  titleMobile = title,
  closeButtonLabel = 'Close this site',
  redirectUrl = 'https://google.com.au',
  dismissLabel = 'Dismiss',
  className,
  ...blockWrapperProps
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const { handleRedirect, handleBlockKeyDown, handleDismiss } = useQuickExit(
    redirectUrl,
    setIsDismissed,
  );

  if (isDismissed) {
    return null;
  }

  return (
    <BlockWrapper
      className={clsx('quickExitBlock', className)}
      {...blockWrapperProps}
      role="alert"
      aria-live="assertive"
      onClick={handleRedirect}
      tabIndex={0}
      onKeyDown={handleBlockKeyDown}
      aria-label="Quick exit banner. Click to exit quickly"
      title="click to exit quickly"
    >
      <ContentWrapper className="quickExitBlock__wrapper">
        <ContentColumn className="quickExitBlock__content">
          {/* Mobile actions */}
          <div className="quickExitBlock__mobile">
            {closeButtonLabel && (
              <Button
                onClick={handleRedirect}
                variant="primary"
                size="base"
                color="brand-1"
                title={closeButtonLabel}
              >
                {closeButtonLabel}
              </Button>
            )}
            <Button
              onClick={handleDismiss}
              variant="primary"
              size="base"
              color="white"
              icon="xmark"
              iconPosition="before"
              aria-label={dismissLabel}
              title={dismissLabel}
            />
          </div>

          {/* Desktop Dismiss Button */}
          <Button
            onClick={handleDismiss}
            variant="primary"
            size="xl"
            color="black"
            icon="xmark"
            iconPosition="before"
            className="quickExitBlock__dismiss"
            title={dismissLabel}
          >
            {dismissLabel}
          </Button>

          {/* Title */}
          <Text html={title} className="quickExitBlock__text" />
          <Text html={titleMobile} className="quickExitBlock__text__mobile" />

          {/* Desktop Actions */}
          <div className="quickExitBlock__actions">
            {closeButtonLabel && (
              <Button
                onClick={handleRedirect}
                variant="primary"
                size="xl"
                color="brand-4"
                className="quickExitBlock__close"
                title={closeButtonLabel}
              >
                {closeButtonLabel}
              </Button>
            )}
          </div>
        </ContentColumn>
      </ContentWrapper>
    </BlockWrapper>
  );
};

export default QuickExitBlock;
